import type { ComponentSize } from '@zoryn/constants'
import type { ComputedRef } from 'vue'
import {
  isGridSpecialColumn,
  refreshVisibleColumns,
} from './meta'
import { resolveCellDisplayText } from './resolve-cell-display-text'
import { applySpecialColumnWidths } from './meta'
import {
  applyHeaderMinWidths,
  estimateHeaderTitleWidth,
} from './resolve-header-min-width'
import type { GridColumnInfo, GridOptions } from '../types'

/** 与 theme-chalk grid body cell 默认 padding 对齐：12px * 2 */
export const GRID_CELL_PADDING_X = 24

export const GRID_CELL_BODY_FONT =
  '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'

const CONTENT_AUTO_WIDTH_SAMPLE = 50
const MIN_AUTO_COLUMN_WIDTH = 60
const MAX_AUTO_COLUMN_WIDTH = 480

const FIXED_TYPE_WIDTHS: Record<string, number> = {
  image: 100,
  file: 180,
  files: 200,
}

let measureCanvas: HTMLCanvasElement | null = null

function getMeasureContext() {
  if (typeof document === 'undefined') {
    return null
  }
  measureCanvas ??= document.createElement('canvas')
  return measureCanvas.getContext('2d')
}

export function isAutoColumnWidthEnabled(options?: GridOptions) {
  return options?.autoColumnWidth !== false
}

export function isColumnWidthSpecified(column: GridColumnInfo) {
  return column.widthSpecified === true
}

export function estimateCellTextWidth(
  text: string,
  font = GRID_CELL_BODY_FONT
): number {
  if (!text) {
    return 0
  }
  const ctx = getMeasureContext()
  if (ctx) {
    ctx.font = font
    const measured = ctx.measureText(text).width
    if (measured > 0) {
      return Math.ceil(measured)
    }
  }
  let width = 0
  for (const char of text) {
    width += char.charCodeAt(0) > 255 ? 14 : 8
  }
  return width
}

export function resolveContentMinWidth(
  column: GridColumnInfo,
  rows: Record<string, unknown>[],
  sampleSize = CONTENT_AUTO_WIDTH_SAMPLE
): number {
  if (isGridSpecialColumn(column) || isColumnWidthSpecified(column)) {
    return Number(column.width) || 0
  }

  const fixedWidth = FIXED_TYPE_WIDTHS[column.type ?? '']
  if (fixedWidth) {
    return fixedWidth
  }

  if (!column.field || !rows.length) {
    return 0
  }

  let maxTextWidth = 0
  const sample = rows.slice(0, sampleSize)
  for (const row of sample) {
    const text = resolveCellDisplayText(row, column)
    maxTextWidth = Math.max(maxTextWidth, estimateCellTextWidth(text))
  }

  const titleWidth = estimateHeaderTitleWidth(column.title || column.field || '')
  maxTextWidth = Math.max(maxTextWidth, titleWidth)

  return Math.min(
    MAX_AUTO_COLUMN_WIDTH,
    Math.max(MIN_AUTO_COLUMN_WIDTH, maxTextWidth + GRID_CELL_PADDING_X)
  )
}

export function applyContentAutoWidths(
  columns: GridColumnInfo[],
  rows: Record<string, unknown>[],
  options?: GridOptions
) {
  if (!isAutoColumnWidthEnabled(options)) {
    return columns
  }

  for (const column of columns) {
    if (isColumnWidthSpecified(column) || isGridSpecialColumn(column)) {
      continue
    }
    const contentMin = resolveContentMinWidth(column, rows)
    if (contentMin <= 0) {
      continue
    }
    const currentWidth = Number(column.width) || 0
    const currentMinWidth = Number(column.minWidth) || 80
    column.width = Math.max(currentWidth, contentMin)
    column.minWidth = Math.max(currentMinWidth, contentMin)
  }

  return columns
}

export function applyFitContainerWidths(
  columns: GridColumnInfo[],
  containerWidth: number,
  options?: GridOptions
) {
  if (!isAutoColumnWidthEnabled(options) || containerWidth <= 0) {
    return columns
  }

  const visibleColumns = columns.filter((col) => col.visible !== false)
  const totalWidth = visibleColumns.reduce(
    (sum, col) => sum + (Number(col.width) || 150),
    0
  )
  const extra = containerWidth - totalWidth
  if (extra <= 1) {
    return columns
  }

  const autoColumns = visibleColumns.filter(
    (col) => !isColumnWidthSpecified(col) && !isGridSpecialColumn(col)
  )
  if (!autoColumns.length) {
    const lastColumn = visibleColumns[visibleColumns.length - 1]
    if (lastColumn && extra > 1) {
      lastColumn.width = (Number(lastColumn.width) || 150) + extra
    }
    return columns
  }

  const autoTotal = autoColumns.reduce(
    (sum, col) => sum + (Number(col.width) || 150),
    0
  )
  const allocations = autoColumns.map((column) => {
    const base = Number(column.width) || 150
    const exact =
      autoTotal > 0 ? (base / autoTotal) * extra : extra / autoColumns.length
    return {
      column,
      base,
      add: Math.floor(exact),
      remainder: exact - Math.floor(exact),
    }
  })

  let remaining = extra - allocations.reduce((sum, item) => sum + item.add, 0)
  allocations.sort((a, b) => b.remainder - a.remainder)
  for (let i = 0; remaining > 0; i += 1, remaining -= 1) {
    allocations[i % allocations.length]!.add += 1
  }

  for (const { column, base, add } of allocations) {
    column.width = base + add
  }

  return columns
}

export interface ColumnWidthGridLike {
  options: GridOptions
  props?: { editable?: boolean }
  gridSize?: ComputedRef<ComponentSize>
  reactData: { tableData: Record<string, unknown>[] }
  internalData: {
    allColumns: GridColumnInfo[]
    visibleColumns: GridColumnInfo[]
    titleList: GridColumnInfo[]
    flatSource?: Record<string, unknown>[]
  }
}

export function resolveColumnWidthRows($grid: ColumnWidthGridLike) {
  const flatSource = $grid.internalData.flatSource
  if (flatSource?.length) {
    return flatSource
  }
  return $grid.reactData.tableData
}

export function recalculateColumnWidths(
  $grid: ColumnWidthGridLike,
  containerWidth?: number
) {
  const rows = resolveColumnWidthRows($grid)
  const columns = $grid.internalData.allColumns

  const size = $grid.gridSize?.value
  applySpecialColumnWidths(columns, size)
  applyHeaderMinWidths(columns, $grid.options, $grid.props, size)
  applyContentAutoWidths(columns, rows, $grid.options)

  if (containerWidth != null && containerWidth > 0) {
    applyFitContainerWidths(columns, containerWidth, $grid.options)
  }

  refreshVisibleColumns($grid)
  return columns
}
